import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Box, Tooltip, IconButton, TextField, FormControlLabel, Checkbox, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip } from '@mui/material';
import { Add, Edit, Inventory2, Print } from '@mui/icons-material';
import { Item, ItemKind, ITEM_NATURE_LABEL, ItemNature } from '../../interfaces/item.interface';
import { ItemCategory } from '../../interfaces/item-category.interface';
import { UnitOfMeasurement } from '../../interfaces/unit-of-measurement.interface';
import { ItemService } from '../../services/item.service';
import { ItemCategoryService } from '../../services/item-category.service';
import { UnitOfMeasurementService } from '../../services/unitOfMeasurement.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';
import ItemSalesFields from './ItemSalesFields';   // FW-2A
import KitComponentsDialog from './KitComponentsDialog';   // F6c
import { costingMethodLabel } from '../../utils/costingMethod';   // G6-0
import StoreLabelDialog from './StoreLabelDialog';   // Q3
import ItemScanField from './ItemScanField';   // Q3
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { fetchAllPages } from '../../services/salesOrder.service';
import { categoryLabel, sortCategoryTree } from '../../utils/itemCategoryTree';

const ItemList: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [categories, setCategories] = useState<ItemCategory[]>([]);
    const [units, setUnits] = useState<UnitOfMeasurement[]>([]);
    const [loading, setLoading] = useState(true);
    // Only the very first load replaces the page; later fetches (search, paging) keep the search
    // box mounted so typing is never interrupted.
    const [hasLoaded, setHasLoaded] = useState(false);
    const toast = useToast();
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);

    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [formData, setFormData] = useState({
        itemCode: '',
        itemName: '',
        barcode: '',   // Q3
        description: '',
        itemCategoryId: '',
        unitOfMeasurementId: '',
        lotTracked: false,
        expiryTracked: false,   // F6a
        serialTracked: false,   // F6b
        standardUnitCost: undefined as number | undefined,
        hsCode: '',
        itemKind: 'STOCK' as ItemKind,   // FW-2A (F6c: KIT)
        sellable: false,
        salePrice: null as number | null,
        defaultRevenueAccountId: null as string | null,
        defaultTaxCodeId: null as string | null,
        nature: '' as ItemNature | ''   // '' = use the category default
    });
    const [natureFilter, setNatureFilter] = useState('');
    const [kindFilter, setKindFilter] = useState('');   // FW-2A
    // ---- Q3: label selection, scan-to-find and ?open=<itemId> (what an item QR opens) ----
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const canManageItems = usePermission(PERMISSIONS.ITEM_MANAGE);
    const canPrintLabels = usePermission(PERMISSIONS.STORE_LABEL_PRINT);
    const canLabel = canManageItems || canPrintLabels;   // Q3; STORE_LABEL_PRINT lets store keepers print too
    const [labelOpen, setLabelOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [kitItem, setKitItem] = useState<Item | null>(null);   // F6c: kit components dialog
    const [sellableOnly, setSellableOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchItems = async (page: number, search: string = '', size: number = rowsPerPage, kind: string = kindFilter, onlySellable: boolean = sellableOnly, nature: string = natureFilter) => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(page, size, search, kind || undefined, onlySellable || undefined, nature || undefined);   // FW-2A
            setItems(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    };

    const fetchCategories = async () => {
        try {
            const all = await fetchAllPages((p, size) => ItemCategoryService.getItemCategoriesByInstitution(p, size));
            setCategories(sortCategoryTree(all));
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const fetchUnits = async () => {
        try {
            const data = await UnitOfMeasurementService.getAll();
            setUnits(data);
        } catch (err) {
            console.error('Error fetching units:', err);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchUnits();
    }, []);

    // Debounce the search term
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    // Fetch items on mount and whenever the debounced search or page size changes (resetting to first page)
    useEffect(() => {
        fetchItems(0, debouncedSearch, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, rowsPerPage, kindFilter, sellableOnly, natureFilter]);

    useEffect(() => {
        const openId = searchParams.get('open');
        if (!openId) return;
        let cancelled = false;
        ItemService.getById(openId)
            .then(item => { if (!cancelled) handleOpenFormModal(item); })
            .catch(err => { if (!cancelled) toast.showApiError(err, 'Could not open that item'); })
            .finally(() => {
                if (cancelled) return;
                const next = new URLSearchParams(searchParams);
                next.delete('open');
                setSearchParams(next, { replace: true });
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get('open')]);

    const toggleSelected = (id: string) =>
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleOpenFormModal = (item?: Item) => {
        if (item) {
            setSelectedItem(item);
            setFormData({
                itemCode: item.itemCode,
                itemName: item.itemName,
                barcode: item.barcode ?? '',   // Q3
                description: item.description,
                itemCategoryId: item.itemCategoryId,
                unitOfMeasurementId: item.unitOfMeasurementId || '',
                lotTracked: item.lotTracked,
                expiryTracked: !!item.expiryTracked,   // F6a
                serialTracked: !!item.serialTracked,   // F6b
                standardUnitCost: item.standardUnitCost,
                hsCode: item.hsCode || '',
                itemKind: item.itemKind ?? 'STOCK',   // FW-2A
                sellable: !!item.sellable,
                salePrice: item.salePrice ?? null,
                defaultRevenueAccountId: item.defaultRevenueAccountId ?? null,
                defaultTaxCodeId: item.defaultTaxCodeId ?? null,
                nature: item.nature ?? ''
            });
        } else {
            setSelectedItem(null);
            setFormData({
                itemCode: '',
                itemName: '',
                barcode: '',   // Q3
                description: '',
                itemCategoryId: '',
                unitOfMeasurementId: '',
                lotTracked: false,
                expiryTracked: false,   // F6a
                serialTracked: false,   // F6b
                standardUnitCost: undefined,
                hsCode: '',
                itemKind: 'STOCK', sellable: false, salePrice: null, defaultRevenueAccountId: null, defaultTaxCodeId: null, nature: ''   // FW-2A
            });
        }
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedItem(null);
        setFormData({
            itemCode: '',
            itemName: '',
            barcode: '',   // Q3
            description: '',
            itemCategoryId: '',
            unitOfMeasurementId: '',
            lotTracked: false,
            expiryTracked: false,   // F6a
            serialTracked: false,   // F6b
            standardUnitCost: undefined,
            hsCode: '',
            itemKind: 'STOCK', sellable: false, salePrice: null, defaultRevenueAccountId: null, defaultTaxCodeId: null, nature: ''   // FW-2A
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (selectedItem) {
                const { nature, ...rest } = formData;
                await ItemService.updateItem(selectedItem.id!, nature ? { ...rest, nature } : { ...rest, useCategoryNature: true });
                toast.showSuccess('Item updated');
            } else {
                const { nature, ...rest } = formData;
                await ItemService.createItem(nature ? { ...rest, nature } : rest);
                toast.showSuccess('Item created');
            }
            await fetchItems(pageInfo.currentPage, debouncedSearch, rowsPerPage);
            handleCloseFormModal();
        } catch (error) {
            console.error('Error saving item:', error);
            toast.showApiError(error, 'Failed to save item');
        }
    };

    if (loading && !hasLoaded) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="store.items.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Item List</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {canLabel && <Button
                    data-help="store.items.printLabels"
                    variant="outlined"
                    startIcon={<Print />}
                    disabled={selectedIds.length === 0}
                    onClick={() => setLabelOpen(true)}
                >
                    Print labels{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </Button>}
                <Button
                    data-help="store.items.create"
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenFormModal()}
                >
                    Create Item
                </Button>
                </Box>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }} data-help-section="store.items.filter">
                <Box data-help="store.items.scanFind" sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                    <ItemScanField compact label="Scan or type a barcode to find an item" data-testid="item-scan-find"
                        onItem={item => { handleOpenFormModal(item); return { ok: true, message: `Found ${item.itemCode} - ${item.itemName}.` }; }} />
                </Box>
                <TextField
                    size="small"
                    sx={{ flex: '1 1 260px', maxWidth: 300 }}
                    data-help="store.items.filter.search" label="Search by item name or code"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                {/* ---- FW-2A: kind / sellable filters ---- */}
                <TextField
                    data-help="store.items.filter.kind" select SelectProps={{ native: true }} size="small" label="Kind" InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 140px', maxWidth: 200 }}
                    value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
                    data-testid="kind-filter"
                >
                    <option value="">All kinds</option>
                    <option value="STOCK">Stock items</option>
                    <option value="SERVICE">Services</option>
                    <option value="KIT">Kits</option>
                </TextField>
                <TextField
                    data-help="store.items.filter.nature" select SelectProps={{ native: true }} size="small" label="Item type" InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 140px', maxWidth: 200 }}
                    value={natureFilter} onChange={(e) => setNatureFilter(e.target.value)}
                    data-testid="nature-filter"
                >
                    <option value="">All types</option>
                    <option value="CONSUMABLE">Consumable</option>
                    <option value="RETURNABLE">Returnable</option>
                    <option value="FIXED_ASSET">Fixed asset</option>
                </TextField>
                <FormControlLabel
                    control={<Checkbox checked={sellableOnly} onChange={(e) => setSellableOnly(e.target.checked)} />}
                    data-help="store.items.filter.sellable" label="Sellable only"
                />
                {/* ---- end FW-2A ---- */}
                <Typography variant="body2" color="text.secondary">
                    Total Items: {pageInfo.totalElements}
                </Typography>
            </Box>

            <TableContainer component={Paper} data-help-section="store.items.table">
            <Table size="small" data-testid="items-table" sx={{ minWidth: 640 }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                        <TableCell data-help="store.items.col.select">
                            {canLabel && <Checkbox size="small" data-help="store.items.selectAll" inputProps={{ 'aria-label': 'Select all items on this page' }}
                                checked={items.length > 0 && items.every(i => selectedIds.includes(i.id!))}
                                indeterminate={items.some(i => selectedIds.includes(i.id!)) && !items.every(i => selectedIds.includes(i.id!))}
                                onChange={(e) => setSelectedIds(prev => e.target.checked
                                    ? Array.from(new Set([...prev, ...items.map(i => i.id!)]))
                                    : prev.filter(x => !items.some(i => i.id === x)))} />}
                        </TableCell>
                        <TableCell data-help="store.items.col.code">Item Code</TableCell>
                        <TableCell data-help="store.items.col.name">Item Name</TableCell>
                        <TableCell data-help="store.items.col.barcode">Barcode</TableCell>{/* Q3 */}
                        <TableCell data-help="store.items.col.description">Description</TableCell>
                        <TableCell data-help="store.items.col.category">Category</TableCell>
                        <TableCell data-help="store.items.col.unit">Unit</TableCell>
                        <TableCell data-help="store.items.col.kind">Kind</TableCell>{/* FW-2A */}
                        <TableCell data-help="store.items.col.nature">Item type</TableCell>
                        <TableCell data-help="store.items.col.salePrice" align="right">Sale price</TableCell>
                        <TableCell data-help="store.items.col.stdCost" align="right">Standard Unit Cost</TableCell>
                        <TableCell data-help="store.items.col.actions">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && (
                        <TableRow>
                            <TableCell align="center" colSpan={12} data-testid="table-loading">Loading...</TableCell>
                        </TableRow>
                    )}
                    {!loading && items.length === 0 && (
                        <TableRow>
                            <TableCell align="center" colSpan={12}>No items found</TableCell>
                        </TableRow>
                    )}
                    {!loading && items.map(item => (
                        <TableRow key={item.id} hover data-testid={`item-row-${item.id}`}>
                            <TableCell>
                                {canLabel && <Checkbox data-help="store.items.action.select" size="small" checked={selectedIds.includes(item.id!)} onChange={() => toggleSelected(item.id!)}
                                    inputProps={{ 'aria-label': `Select ${item.itemCode}` }} />}
                            </TableCell>
                            <TableCell>{item.itemCode}</TableCell>
                            <TableCell>{item.itemName}</TableCell>
                            <TableCell>{item.barcode || '-'}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{(() => { const c = categories.find(x => x.id === item.itemCategoryId); return c ? categoryLabel(c) : ''; })()}</TableCell>
                            <TableCell>{item.unit ? `${item.unit.name} (${item.unit.symbol})` : '-'}</TableCell>
                            <TableCell>{item.itemKind === 'SERVICE' ? 'Service' : item.itemKind === 'KIT' ? 'Kit' : 'Stock'}{item.sellable ? ' (sellable)' : ''}{(item.itemKind ?? 'STOCK') === 'STOCK' && (<Typography component="div" variant="caption" color="text.secondary" data-testid={`item-costing-${item.id}`}>{costingMethodLabel(item)}</Typography>)}</TableCell>{/* FW-2A, F6c, G6-0 */}
                            <TableCell>
                                {item.effectiveNature && (
                                    <Chip size="small" data-testid={`item-nature-${item.id}`}
                                        color={item.effectiveNature === 'RETURNABLE' ? 'info' : item.effectiveNature === 'FIXED_ASSET' ? 'warning' : 'default'}
                                        variant={item.nature ? 'filled' : 'outlined'}
                                        label={ITEM_NATURE_LABEL[item.effectiveNature]} />
                                )}
                            </TableCell>
                            <TableCell align="right">{item.sellable && item.salePrice != null ? formatMoney(item.salePrice) : '-'}</TableCell>
                            <TableCell align="right">{item.standardUnitCost != null ? formatMoney(item.standardUnitCost) : '-'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip title="Edit Item" arrow>
                                    <IconButton
                                        data-help="store.items.action.edit"
                                        size="small"
                                        onClick={() => handleOpenFormModal(item)}
                                        color="primary"
                                        aria-label="Edit Item"
                                    >
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                {item.itemKind === 'KIT' && (
                                    <Tooltip title="Kit components" arrow>
                                        <IconButton data-help="store.items.action.kit" size="small" color="primary" aria-label="Kit components" onClick={() => setKitItem(item)}>
                                            <Inventory2 />
                                        </IconButton>
                                    </Tooltip>
                                )}{/* F6c */}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                component="div"
                data-testid="items-pagination"
                count={pageInfo.totalElements}
                page={pageInfo.currentPage}
                onPageChange={(_, p) => fetchItems(p, debouncedSearch, rowsPerPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
            />
            </TableContainer>

            {/* Form dialog (polish 4: a real MUI Dialog, role=dialog labelled by its title) */}
            <Dialog
                open={isFormModalOpen}
                onClose={handleCloseFormModal}
                maxWidth="xs"
                fullWidth
                data-help-page="store.itemForm.page" aria-labelledby="item-form-title"
            >
                <DialogTitle id="item-form-title">{selectedItem ? 'Edit Item' : 'Create Item'}</DialogTitle>
                    <form onSubmit={handleSubmit}>
                    <DialogContent sx={{ pt: 0 }}>
                        <TextField
                            fullWidth
                            data-help="store.itemForm.field.code" label="Item Code"
                            value={formData.itemCode}
                            onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })}
                            required
                            margin="normal"
                        />
                        {/* ---- Q3: optional barcode, unique in the company; blank clears it ---- */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }} data-help-section="store.items.barcodeField">
                            <TextField
                                data-help="store.itemForm.field.barcode" label="Barcode (optional)"
                                value={formData.barcode}
                                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                margin="normal"
                                sx={{ flex: '1 1 200px' }}
                                inputProps={{ maxLength: 64 }}
                                helperText="Scanned at receipt, issue, transfer and count. Leave blank to scan the item code."
                                data-testid="barcode-input"
                            />
                            <Button
                                data-help="store.items.generateBarcode"
                                variant="outlined"
                                size="small"
                                disabled={!formData.itemCode.trim()}
                                onClick={async () => {
                                    try {
                                        const b = await ItemService.suggestBarcode(formData.itemCode.trim(), selectedItem?.id);
                                        setFormData(fd => ({ ...fd, barcode: b }));
                                    } catch (err) {
                                        toast.showApiError(err, 'Failed to generate a barcode');
                                    }
                                }}
                            >
                                Generate
                            </Button>
                        </Box>
                        {/* ---- end Q3 ---- */}
                        <TextField
                            fullWidth
                            data-help="store.itemForm.field.name" label="Item Name"
                            value={formData.itemName}
                            onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                            required
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            data-help="store.itemForm.field.description" label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <SearchableSelect<ItemCategory>
                            options={categories}
                            value={formData.itemCategoryId}
                            onChange={id => setFormData({ ...formData, itemCategoryId: id })}
                            getOptionLabel={categoryLabel}
                            data-help="store.itemForm.field.category" label="Category"
                            required
                            sx={{ mt: 2, mb: 1 }}
                            data-testid="category-select"
                        />
                        <SearchableSelect<UnitOfMeasurement>
                            options={units}
                            value={formData.unitOfMeasurementId}
                            onChange={id => setFormData({ ...formData, unitOfMeasurementId: id })}
                            getOptionLabel={unit => `${unit.name} (${unit.symbol})${unit.isGlobal ? '' : ' — Custom'}`}
                            data-help="store.itemForm.field.unit" label="Unit of Measurement"
                            required
                            sx={{ mt: 2, mb: 1 }}
                            data-testid="unit-select"
                        />
                        <TextField
                            select
                            fullWidth
                            data-help="store.itemForm.field.nature" label="Item type"
                            value={formData.nature}
                            onChange={(e) => setFormData({ ...formData, nature: e.target.value as ItemNature | '' })}
                            margin="normal"
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Returnable items must be issued to a person or department and come back to the store. Fixed assets belong in the Fixed Assets register."
                            data-testid="item-nature-select"
                        >
                            <option value="">{`Use category default (${ITEM_NATURE_LABEL[categories.find(c => c.id === formData.itemCategoryId)?.effectiveNature ?? 'CONSUMABLE']})`}</option>
                            <option value="CONSUMABLE">Consumable</option>
                            <option value="RETURNABLE">Returnable</option>
                            <option value="FIXED_ASSET">Fixed asset</option>
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.lotTracked}
                                    disabled={formData.itemKind === 'KIT'}
                                    onChange={(e) => setFormData({ ...formData, lotTracked: e.target.checked, expiryTracked: e.target.checked ? formData.expiryTracked : false })}
                                />
                            }
                            data-help="store.itemForm.field.lot" label="Lot Tracked"
                        />
                        {/* ---- G6-0: effective costing method; the flag cannot change while the item holds stock ---- */}
                        {(formData.itemKind ?? 'STOCK') === 'STOCK' && (
                            <Box sx={{ fontSize: '0.8rem', color: 'text.secondary', ml: 4, mt: -0.5, mb: 0.5 }} data-testid="item-form-costing">
                                {costingMethodLabel({ lotTracked: formData.lotTracked })}
                                {selectedItem ? ' (lot tracking can only change while the item holds no stock)' : ''}
                            </Box>
                        )}
                        {/* ---- end G6-0 ---- */}
                        {/* ---- F6a: expiry tracking (needs lot tracking) ---- */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.expiryTracked}
                                    disabled={!formData.lotTracked}
                                    onChange={(e) => setFormData({ ...formData, expiryTracked: e.target.checked })}
                                    inputProps={{ 'aria-label': 'Expiry tracked' }}
                                />
                            }
                            data-help="store.itemForm.field.expiry" label={formData.lotTracked ? 'Expiry tracked (expiry date required on receipt; first-expiry-first-out)' : 'Expiry tracked (turn on lot tracking first)'}
                        />
                        {/* ---- end F6a ---- */}
                        {/* ---- F6b: serial tracking (stock items only) ---- */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.serialTracked}
                                    disabled={formData.itemKind !== 'STOCK'}
                                    onChange={(e) => setFormData({ ...formData, serialTracked: e.target.checked })}
                                    inputProps={{ 'aria-label': 'Serial tracked' }}
                                />
                            }
                            data-help="store.itemForm.field.serial" label="Serial tracked (one serial number per unit, captured on receipt and picked on issue or sale)"
                        />
                        {/* ---- end F6b ---- */}
                        <TextField
                            fullWidth
                            data-help="store.itemForm.field.stdCost" label="Standard Unit Cost"
                            type="number"
                            value={formData.standardUnitCost ?? ''}
                            onChange={(e) => {
                                const raw = e.target.value;
                                setFormData({
                                    ...formData,
                                    standardUnitCost: raw === '' ? undefined : Number(raw)
                                });
                            }}
                            margin="normal"
                            InputProps={{
                                inputProps: { min: 0, step: 'any' }
                            }}
                            data-testid="standard-unit-cost-input"
                        />
                        {/* ---- B4-A: HS code ---- */}
                        <TextField
                            fullWidth
                            data-help="store.itemForm.field.hsCode" label="HS code (optional)"
                            value={formData.hsCode}
                            onChange={(e) => setFormData({ ...formData, hsCode: e.target.value })}
                            margin="normal"
                            inputProps={{ maxLength: 20 }}
                            data-testid="hs-code-input"
                        />
                        {/* ---- end B4-A ---- */}
                        {/* ---- FW-2A: kind + sales section ---- */}
                        <TextField
                            data-help="store.itemForm.field.kind" select SelectProps={{ native: true }} fullWidth margin="normal" label="Kind"
                            value={formData.itemKind}
                            onChange={(e) => { const k = e.target.value as ItemKind; const noLots = k !== 'STOCK'; setFormData({ ...formData, itemKind: k, lotTracked: noLots ? false : formData.lotTracked, expiryTracked: noLots ? false : formData.expiryTracked, serialTracked: noLots ? false : formData.serialTracked }); }}
                            helperText={formData.itemKind === 'SERVICE' ? 'A service carries no stock: it cannot be received, issued or transferred.'
                                : formData.itemKind === 'KIT' ? 'A kit (or package product) holds no stock of its own: after saving, use the kit button on its row to set its components.' : undefined}
                            data-testid="item-kind-select"
                        >
                            <option value="STOCK">Stock item</option>
                            <option value="SERVICE">Service</option>
                            <option value="KIT">Kit / package product</option>
                        </TextField>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.sellable}
                                    onChange={(e) => setFormData({ ...formData, sellable: e.target.checked })}
                                    inputProps={{ 'aria-label': 'Sellable' }}
                                />
                            }
                            data-help="store.itemForm.field.sellable" label="Sellable (appears in the sales catalogue)"
                        />
                        {formData.sellable && (
                            <ItemSalesFields value={formData} onChange={patch => setFormData(fd => ({ ...fd, ...patch }))} />
                        )}
                        {/* ---- end FW-2A ---- */}
                    </DialogContent>
                    <DialogActions>
                        {selectedItem && canLabel && (
                            <Button data-help="store.items.printOneLabel" startIcon={<Print />} sx={{ mr: 'auto' }}
                                onClick={() => setLabelOpen(true)}>
                                Print label
                            </Button>
                        )}
                        <Button data-help="store.itemForm.cancel" onClick={handleCloseFormModal}>
                            Cancel
                        </Button>
                        <Button data-help="store.itemForm.save" type="submit" variant="contained" color="primary">
                            {selectedItem ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                    </form>
            </Dialog>

            {labelOpen && (
                <StoreLabelDialog open kind="ITEM"
                    ids={isFormModalOpen && selectedItem?.id ? [selectedItem.id] : selectedIds}
                    onClose={() => setLabelOpen(false)} />
            )}{/* Q3 */}
            <KitComponentsDialog open={!!kitItem} kit={kitItem} onClose={() => setKitItem(null)} />{/* F6c */}
        </Box>
    );
};

export default ItemList;
